/**
 * AskDoubtButton Component
 *
 * Voice-based doubt interface. Lives at the top of the chat panel.
 * No popup / modal - status is inline, answer flows into the chat.
 *
 * Flow:
 *   idle -> (pause narrator) -> recording -> processing -> reading -> idle
 *
 * Props:
 *   sessionId          - Session UUID for API calls
 *   chapterIndex       - Current chapter index
 *   isNarratorPlaying  - True when the iframe narrator is speaking
 *   onPauseNarrator    - () => Promise<void> - pauses the iframe narrator
 *   onMessageAdded     - ({ role, content, isVoice }) => void - adds to chat
 *   selectedText       - Highlighted text from chapter iframe
 *   doubtTurns, doubtLimit - accepted for compatibility; there is no limit any more
 *   onDoubtUsed        - () => void - called after each successful doubt
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import CloseIcon from "@mui/icons-material/Close";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { sendVoiceDoubt } from "./aiTutorApi";

const AskDoubtButton = ({
  sessionId,
  chapterIndex,
  isNarratorPlaying = false,
  onPauseNarrator,
  onMessageAdded,
  selectedText = "",
  section = "",
  getScreen,
  getAbortSignal,
  doubtTurns = 0,
  doubtLimit = 50,
  onDoubtUsed,
}) => {
  // 'idle' | 'waiting' | 'recording' | 'processing' | 'reading'
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  // Stop mic stream
  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  // Step 1: Stop narrator + open mic
  const handleAskDoubt = async () => {
    if (phase !== "idle") return;
    setError(null);
    setPhase("waiting");

    // Ask the narrator to finish the current segment then pause
    await onPauseNarrator?.();

    // We might have been cancelled during the wait
    if (streamRef.current === "cancelled") {
      streamRef.current = null;
      return;
    }

    // Small buffer for the audio device to fully drain before opening the mic
    await new Promise((r) => setTimeout(r, 300));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(100);
      setPhase("recording");
    } catch {
      setError(
        "Microphone access denied. Please allow microphone access and try again."
      );
      setPhase("idle");
    }
  };

  // Step 2: Stop recording -> STT -> RAG
  const handleSend = async () => {
    if (phase !== "recording") return;

    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    stopStream();
    setPhase("processing");

    // Give MediaRecorder time to flush the last chunk
    await new Promise((r) => setTimeout(r, 200));

    const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    if (blob.size < 1000) {
      setError("Recording was too short - please try again.");
      setPhase("idle");
      return;
    }

    const audioFile = new File([blob], "doubt.webm", { type: "audio/webm" });

    try {
      const data = await sendVoiceDoubt(
        sessionId,
        audioFile,
        chapterIndex,
        selectedText,
        section || "",
        null,
        // The reader's, not this button's: collapsing the pane unmounts the
        // button, and that must not abandon a question already asked.
        getAbortSignal?.(),
        getScreen?.() || {}
      );

      const { transcript, response, response_audio } = data;

      // Push both turns into the chat window
      if (transcript) {
        onMessageAdded?.({ role: "user", content: transcript, isVoice: true });
      }
      if (response) {
        onMessageAdded?.({
          role: "assistant",
          content: response,
          isVoice: true,
        });
      }

      onDoubtUsed?.();

      // Step 3: Read the answer aloud if TTS audio is provided
      if (response_audio) {
        setPhase("reading");
        await playBase64Audio(response_audio);
      }

      setPhase("idle");
    } catch (err) {
      if (err?.name === "AbortError") return;      // the reader was closed
      // The backend and the tutor both send a message meant for the student.
      const data = err.response?.data || {};
      setError(data.error || data.detail || "Something went wrong. Please try again.");
      setPhase("idle");
    }
  };

  // Cancel at any point
  const handleCancel = () => {
    if (phase === "waiting") {
      streamRef.current = "cancelled";
    } else if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current?.stop();
      stopStream();
    } else {
      stopStream();
    }
    audioRef.current?.pause();
    audioRef.current = null;
    audioChunksRef.current = [];
    setPhase("idle");
    setError(null);
  };

  // Stop reading mid-playback
  const handleStopReading = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPhase("idle");
  };

  // Play base64 WAV audio
  const playBase64Audio = (b64) => {
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "audio/wav" });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audioRef.current = audio;
    return new Promise((resolve) => {
      audio.onended = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      audio.play();
    });
  };

  // There is no limit on questions (2026-10-13), so no counter and no "exhausted".
  const exhausted = false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* IDLE: main trigger button */}
      {!exhausted && phase === "idle" && (
        <Button
          variant="contained"
          startIcon={<MicIcon />}
          onClick={handleAskDoubt}
          fullWidth
          sx={{
            bgcolor: "#1e40af",
            "&:hover": { bgcolor: "#1e3a8a" },
            py: 1,
          }}
        >
          Ask a Question
        </Button>
      )}

      {/* WAITING (for narrator to pause) */}
      {!exhausted && phase === "waiting" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#ede9fe",
              px: 2,
              py: 1,
              borderRadius: 2,
              border: "1px solid #c4b5fd",
            }}
          >
            <CircularProgress size={16} sx={{ color: "#1e40af" }} />
            <Typography variant="body2" sx={{ color: "#1e40af" }}>
              Waiting for narration to pause...
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleCancel}
            fullWidth
            size="small"
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* RECORDING */}
      {!exhausted && phase === "recording" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: "#ef4444",
                borderRadius: "50%",
                animation: "pulse 1s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <Typography variant="body2" color="error.main" fontWeight={500}>
              Recording... speak your question
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              sx={{
                flex: 1,
                bgcolor: "#2563eb",
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Send
            </Button>
            <IconButton onClick={handleCancel} sx={{ bgcolor: "#f3f4f6" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* PROCESSING */}
      {!exhausted && phase === "processing" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          <CircularProgress size={16} sx={{ color: "#2563eb" }} />
          <Typography variant="body2" color="primary">
            Understanding your question...
          </Typography>
        </Box>
      )}

      {/* READING */}
      {!exhausted && phase === "reading" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VolumeUpIcon
              sx={{
                color: "#16a34a",
                animation: "pulse 1s infinite",
              }}
            />
            <Typography variant="body2" sx={{ color: "#16a34a" }}>
              Reading answer aloud...
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<StopIcon />}
            onClick={handleStopReading}
          >
            Stop
          </Button>
        </Box>
      )}

      {/* ERROR */}
      {error && (
        <Box
          sx={{
            bgcolor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="caption" color="error.main">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AskDoubtButton;
